import type { CSSProperties } from 'react';
import type { ShapeLayout } from '@/core/instruments/shapes';
import styles from './FretDiagram.module.css';

/** Fret height in px (design: Player.dc.html). */
const FRET = 34;
const DOT = 22;
const DOT_SLOTS = 4;

interface FretDiagramProps {
  /** null draws an empty board (no chord). */
  layout: ShapeLayout | null;
  strings: number;
  /** Distance between strings in px: 32 for guitar, 40 for ukulele. */
  spacing: number;
}

/**
 * A chord diagram whose finger dots and barre slide to the next shape (design: diagram morph
 * rules). Dots fill 4 fixed slots in string order; unused slots park on the last used dot with
 * opacity 0, so they fade rather than fly in.
 */
export function FretDiagram({ layout, strings, spacing }: FretDiagramProps) {
  const dots = layout?.dots ?? [];
  const slots = Array.from({ length: DOT_SLOTS }, (_, k) => {
    const d = dots[k] ?? dots.at(-1) ?? { string: 0, fret: 1 };
    return { ...d, visible: k < dots.length };
  });
  const barre = layout?.barre;
  const markers = layout?.markers ?? Array.from({ length: strings }, () => '');
  const width = strings * spacing;

  const dotStyle = (s: { string: number; fret: number; visible: boolean }): CSSProperties => ({
    left: s.string * spacing - (DOT / 2 - 1),
    top: (s.fret - 1) * FRET + (FRET - DOT) / 2,
    opacity: s.visible ? 1 : 0,
  });
  const barreStyle: CSSProperties = barre
    ? {
        left: Math.min(barre.fromString, barre.toString) * spacing - (DOT / 2 - 1),
        width: Math.abs(barre.toString - barre.fromString) * spacing + DOT,
        top: (barre.fret - 1) * FRET + (FRET - DOT) / 2,
        opacity: 1,
      }
    : { left: -(DOT / 2 - 1), width: DOT, top: (FRET - DOT) / 2, opacity: 0 };

  return (
    <div className={styles.diagram} style={{ width }} aria-hidden="true">
      <div
        className={styles.markers}
        style={{ gridTemplateColumns: `repeat(${strings}, ${spacing}px)` }}
      >
        {markers.map((m, i) => (
          <span key={i} data-marker={m} />
        ))}
      </div>
      <div
        className={styles.board}
        data-nut={layout?.baseFret === 1 || !layout || undefined}
        style={
          {
            width: (strings - 1) * spacing + 2,
            height: 4 * FRET,
            marginLeft: spacing / 2 - 1,
            '--spacing': `${spacing}px`,
            '--fret': `${FRET}px`,
          } as CSSProperties
        }
      >
        <span className={styles.barre} style={barreStyle} />
        {slots.map((s, k) => (
          <span key={k} className={styles.dot} style={dotStyle(s)} />
        ))}
      </div>
      {layout && layout.baseFret > 1 && (
        <span className={styles.baseFret} data-label={`${layout.baseFret}fr`} />
      )}
    </div>
  );
}
